<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { housekeepingApi, noWorkApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import type { BoardCard, ContributionKind, NoWorkDay } from '$lib/types';

  const {
    ownCards,
    recentDays,
    onClose,
    onSuccess
  }: { ownCards: BoardCard[]; recentDays: NoWorkDay[]; onClose: () => void; onSuccess: () => void } = $props();

  const housekeepingQuery = createQuery(() => ({
    queryKey: ['housekeeping'],
    queryFn: () => housekeepingApi.list()
  }));

  import type { MessageKey } from '$lib/i18n/en';

  const KINDS: { id: ContributionKind; labelKey: MessageKey }[] = [
    { id: 'housekeeping', labelKey: 'noWork.kind.housekeeping' },
    { id: 'own_card', labelKey: 'noWork.kind.ownCard' },
    { id: 'note', labelKey: 'noWork.kind.note' }
  ];

  let kind = $state<ContributionKind>('housekeeping');
  let housekeepingItemId = $state<number | null>(null);
  let boardCardId = $state<number | null>(null);
  let note = $state('');
  let submitting = $state(false);
  let error = $state('');

  // Exactly one contribution ref is required per kind (mirrors board.go's
  // CreateNoWorkDay validation) — the submit button stays disabled until
  // one is actually chosen, so a bare/empty no-work submit is impossible.
  const isValid = $derived(
    kind === 'housekeeping'
      ? housekeepingItemId != null
      : kind === 'own_card'
        ? boardCardId != null
        : note.trim().length > 0
  );

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function describeDay(d: NoWorkDay): string {
    if (d.contribution_kind === 'note') return d.note;
    if (d.contribution_kind === 'own_card') return t('noWork.describe.ownCard');
    return t('noWork.describe.housekeeping');
  }

  async function submit() {
    if (!isValid) {
      error = t('noWork.error.pickOne');
      return;
    }
    submitting = true;
    error = '';
    try {
      await noWorkApi.create({
        contribution_kind: kind,
        housekeeping_item_id: kind === 'housekeeping' ? (housekeepingItemId ?? undefined) : undefined,
        board_card_id: kind === 'own_card' ? (boardCardId ?? undefined) : undefined,
        note: kind === 'note' ? note.trim() : ''
      });
      onSuccess();
      onClose();
    } catch (err) {
      // Handles the 409 (already logged today) gracefully — shown inline,
      // not a crash; the board page's own "Logged today" state normally
      // keeps this from happening, this just covers the race (e.g. a
      // second tab) that slips past it.
      error = getErrorMessage(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal title={t('noWork.title')} {onClose}>
  <div class="space-y-4">
    <p class="text-[13px] text-dark-gray">
      {t('noWork.intro')}
    </p>

    <div class="flex flex-wrap gap-2">
      {#each KINDS as k (k.id)}
        <button type="button" class="tym-chip {kind === k.id ? 'active' : ''}" onclick={() => (kind = k.id)}>
          {t(k.labelKey)}
        </button>
      {/each}
    </div>

    {#if kind === 'housekeeping'}
      <div>
        <div class="label">{t('noWork.housekeepingItem')}</div>
        {#if housekeepingQuery.isLoading}
          <p class="text-[12.5px] text-dark-gray">{t('common.loading')}</p>
        {:else if (housekeepingQuery.data ?? []).length === 0}
          <p class="text-[12.5px] text-dark-gray">{t('noWork.noHousekeeping')}</p>
        {:else}
          <select class="input" aria-label={t('noWork.housekeepingItem')} bind:value={housekeepingItemId}>
            <option value={null}>{t('noWork.choose')}</option>
            {#each housekeepingQuery.data ?? [] as item (item.id)}
              <option value={item.id}>{item.title}</option>
            {/each}
          </select>
        {/if}
      </div>
    {:else if kind === 'own_card'}
      <div>
        <div class="label">{t('noWork.yourCard')}</div>
        {#if ownCards.length === 0}
          <p class="text-[12.5px] text-dark-gray">{t('noWork.noCards')}</p>
        {:else}
          <select class="input" aria-label={t('noWork.yourCard')} bind:value={boardCardId}>
            <option value={null}>{t('noWork.choose')}</option>
            {#each ownCards as c (c.id)}
              <option value={c.id}>{c.title}</option>
            {/each}
          </select>
        {/if}
      </div>
    {:else}
      <div>
        <div class="label">{t('noWork.note')}</div>
        <textarea
          class="input"
          rows="2"
          aria-label={t('noWork.note')}
          placeholder={t('noWork.notePlaceholder')}
          bind:value={note}
        ></textarea>
      </div>
    {/if}

    {#if error}<div class="tym-error-state">{error}</div>{/if}

    <div class="flex justify-end gap-2 pt-1">
      <button type="button" class="btn-secondary" onclick={onClose}>{t('common.cancel')}</button>
      <button type="button" class="btn-primary" disabled={submitting || !isValid} onclick={submit}>
        {submitting ? t('common.saving') : t('noWork.logIt')}
      </button>
    </div>

    {#if recentDays.length > 0}
      <div class="border-t border-toyama-cloudy-gray pt-3">
        <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">{t('noWork.recent')}</p>
        <ul class="space-y-1 text-[12px] text-dark-gray">
          {#each recentDays.slice(0, 5) as d (d.id)}
            <li>{fmtDate(d.day)} — {describeDay(d)}</li>
          {/each}
        </ul>
      </div>
    {/if}
  </div>
</Modal>
